import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Upload } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import type { Case } from "../../types";
import Button from "../../components/common/ui/button/Button";
import Label from "../../components/common/form/Label";
import Breadcrumb from "../../components/common/common/Breadcrumb";

export const UploadFilePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setAlert } = useAlert();

  const caseId = searchParams.get("caseId");
  const [caseItem, setCaseItem] = useState<Case | null>(null);
  const [uploading, setUploading] = useState(false);

  // Form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [expiration, setExpiration] = useState<"7days" | "1week" | "1month">(
    "1week"
  );
  const [explodeAfterView, setExplodeAfterView] = useState(false);

  useEffect(() => {
    if (caseId) {
      loadCase();
    }
  }, [caseId]);

  const loadCase = async () => {
    if (!caseId) return;

    try {
      // Get the case by fetching all cases (API limitation workaround)
      const orgs = await apiClient.listOrganizations();
      for (const org of orgs) {
        const cases = await apiClient.listCases(org.id);
        const foundCase = cases.find((c) => c.id === caseId);
        if (foundCase) {
          setCaseItem(foundCase);
          return;
        }
      }

      setAlert({
        variant: "error",
        title: "Error",
        message: "Case not found",
      });
      navigate("/files");
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to load case",
      });
      navigate("/files");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !caseItem) return;

    setUploading(true);
    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await uploadFile.arrayBuffer();
      const content = new Uint8Array(arrayBuffer);

      // Convert to base64 for transmission
      const base64Content = btoa(String.fromCharCode(...content));

      // For now, this is a placeholder - you'll need to implement client-side encryption
      // before sending to the backend
      const uploadData = {
        caseId: caseItem.id,
        encryptedFilename: btoa(uploadFile.name),
        filenameIv: "placeholder-iv",
        fileType: uploadFile.type,
        fileSize: uploadFile.size,
        encryptedContent: base64Content,
        contentIv: "placeholder-iv",
        encryptionKey: "placeholder-key",
        expiration,
        explodeAfterView,
      };

      await apiClient.uploadFile(uploadData);
      setAlert({
        variant: "success",
        title: "Success",
        message: "File uploaded successfully",
      });
      navigate("/files");
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to upload file",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleCancel = () => {
    navigate("/files");
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  };

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[{ label: "Files", path: "/files" }, { label: "Upload File" }]}
      />

      <div className="flex items-center gap-3">
        <Upload size={32} className="text-brand-500" />
        <div>
          <h1 className="text-title-md2 font-semibold text-black dark:text-white">
            Upload File
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {caseItem ? `Uploading file to "${caseItem.title}"` : "Loading..."}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label>
              Select File <span className="text-error-500">*</span>
            </Label>
            <input
              type="file"
              onChange={handleFileChange}
              required
              className="w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
            {uploadFile && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Selected: {uploadFile.name} ({formatFileSize(uploadFile.size)})
              </p>
            )}
          </div>

          <div>
            <Label>
              Expiration <span className="text-error-500">*</span>
            </Label>
            <select
              value={expiration}
              onChange={(e) =>
                setExpiration(e.target.value as "7days" | "1week" | "1month")
              }
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="7days">7 Days</option>
              <option value="1week">1 Week</option>
              <option value="1month">1 Month</option>
            </select>
          </div>

          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={explodeAfterView}
                onChange={(e) => setExplodeAfterView(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500 dark:border-gray-700"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Delete file after first view
              </span>
            </label>
            <p className="ml-6 mt-1 text-xs text-gray-500 dark:text-gray-400">
              When enabled, the file will be automatically deleted after it's
              viewed once
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={uploading || !caseItem || !uploadFile}
            >
              {uploading ? "Uploading..." : "Upload File"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
